/**
 * GeminiProxyClient
 *
 * Routes Gemini API calls through the ai-proxy backend when available,
 * so the API key never reaches the browser.
 *
 * Fallback: When shouldUseProxy() returns false, callers use the existing
 * direct-to-Gemini code paths (unchanged).
 */

import { AuthService } from '../auth/AuthService';
import type {
  ProxyGenerateContentRequest,
  ProxyVideoRequest,
  VideoStatusResponse,
} from './types';

// ─── Proxy Base URL ──────────────────────────────────────

/**
 * AI_PROXY_URL should be the full base URL including /api/ai path.
 * In development, Vite proxy forwards /api/ai/* to localhost:3001.
 * In production, AI_PROXY_URL points to https://ai-proxy.duckdns.org/api/ai.
 * Fallback /api/ai is a relative URL for local dev only.
 */
function getProxyBaseUrl(): string {
  return import.meta.env.AI_PROXY_URL || '/api/ai';
}

function getAuthHeaders(): Record<string, string> {
  const token = AuthService.getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ─── Decision Logic ──────────────────────────────────────

/**
 * Always route Gemini calls through the backend proxy.
 * The proxy holds the API key server-side so it never reaches the browser.
 */
export function shouldUseProxy(): boolean {
  return true;
}

// ─── Non-Streaming: generateContent ──────────────────────

/**
 * Proxy for Gemini generateContent.
 * Used by: ImageGeneratorService, IdeaCanvasChatService (image mode), SlotScreenGeneratorService (inpainting).
 *
 * Returns the same structure as the Gemini API response.
 */
export async function proxyGenerateContent(
  request: ProxyGenerateContentRequest,
  signal?: AbortSignal,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  const base = getProxyBaseUrl();
  const response = await fetch(`${base}/generate-content`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let message = `AI proxy error (${response.status})`;
    try {
      const parsed = JSON.parse(errorBody);
      message = parsed.message || message;
    } catch {
      if (errorBody) message = errorBody;
    }
    throw new Error(message);
  }

  return response.json();
}

// ─── Streaming: generateContentStream ────────────────────

/**
 * Proxy for Gemini streamGenerateContent via SSE.
 * Used by: IdeaCanvasChatService (text chat mode).
 *
 * Calls onChunk for each SSE event (parsed JSON).
 */
export async function proxyGenerateContentStream(
  request: ProxyGenerateContentRequest,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChunk: (data: any) => void,
  signal?: AbortSignal,
): Promise<void> {
  const base = getProxyBaseUrl();
  const response = await fetch(`${base}/generate-content-stream`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let message = `AI proxy stream error (${response.status})`;
    try {
      const parsed = JSON.parse(errorBody);
      message = parsed.message || message;
    } catch {
      if (errorBody) message = errorBody;
    }
    throw new Error(message);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (payload === '[DONE]') return;

      // Check for error events
      try {
        const parsed = JSON.parse(payload);
        if (parsed.error) {
          throw new Error(parsed.error);
        }
        onChunk(parsed);
      } catch (e) {
        if (e instanceof Error && e.message !== payload) throw e;
        // Skip malformed JSON lines
      }
    }
  }
}

// ─── Video: Start Generation ─────────────────────────────

/**
 * Start Veo video generation via proxy. Returns the operation name for polling.
 */
export async function proxyStartVideoGeneration(
  request: ProxyVideoRequest,
  signal?: AbortSignal,
): Promise<string> {
  const base = getProxyBaseUrl();
  const response = await fetch(`${base}/generate-video`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let message = `AI proxy video error (${response.status})`;
    try {
      const parsed = JSON.parse(errorBody);
      message = parsed.message || message;
    } catch {
      if (errorBody) message = errorBody;
    }
    throw new Error(message);
  }

  const data = await response.json();
  return data.operationName;
}

// ─── Video: Poll Status ──────────────────────────────────

/**
 * Poll video generation status.
 * When completed, the response is binary MP4 (Content-Type: video/mp4).
 * When pending/failed, the response is JSON.
 */
export async function proxyPollVideoStatus(
  operationName: string,
  signal?: AbortSignal,
): Promise<VideoStatusResponse> {
  const base = getProxyBaseUrl();
  const response = await fetch(
    `${base}/video-status?op=${encodeURIComponent(operationName)}`,
    {
      headers: getAuthHeaders(),
      signal,
    },
  );

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`AI proxy video status error (${response.status}): ${errorBody}`);
  }

  // Check if the response is a video (completed)
  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.startsWith('video/')) {
    const videoBlob = await response.blob();
    return { status: 'completed', videoBlob };
  }

  // JSON response (pending or failed)
  const data = await response.json();
  if (data.status === 'failed') {
    return { status: 'failed', error: data.error || 'Video generation failed' };
  }

  return { status: 'pending' };
}
